import { FC, HTMLAttributes, JSXElementConstructor, ReactElement, ReactNode } from "react";
import { Menu, MenuButton, MenuItem, SubMenu } from "./Helpers";
import classNames from "classnames";
import { ReactComponent as Caret } from "src/Themes/assets/menu-right.svg";
import { MenuAlign, MenuButtonModifiers, MenuDirection, RenderProp } from "@szhsin/react-menu";
import st from "./DropdownBtn.module.scss";

/** Тип описывающий элемент списка менюшки */
export type DropDownItemType = {
	/** ИД пункта меню ( опционально ) */
	id?: string;
	/** Флаг указывающий на наличии ссылки с атрибутом download */
	isDownLoadLink?: boolean;
	/** Заголовок ( label ) пункта меню */
	title: string;
	/** Иконка пункта меню ( опционально ) */
	icon?: ReactNode,
	/** Класс пункта меню ( опционально ) */
	className?: string,
	/** Доступность пункта меню ( опционально ) */
	disable?: boolean;
	/** Автозакрытие менюшки после клика ( опционально ) */
	autoClose?: boolean;
	/** Функция будет вызвана при клике на пункте меню ( опционально ) */
	action?: ( e: any ) => void;
	/** Флаг указывающий на активное состояния пункта меню ( опционально ) */
	isActive?: boolean;
	/** Флаг включающий отображения треугольничка ( опционально, используется для вложенных списков ) */
	caret?: boolean;
	/** Массив с вложенными пунктами меню ( опционально ) */
	subItem?: Array<DropDownItemType>;
};

interface IDropdownBtnProps extends HTMLAttributes<HTMLDivElement> {
	hint?: string;
	align?: MenuAlign;
	direction?: MenuDirection;
	menuItems: Array<DropDownItemType>;
	menuButton?: RenderProp<Readonly<{open: boolean;}>, ReactElement<any, string | JSXElementConstructor<any>>>
};

/** Компонента меню ( многоуровневое ) */
const DropdownBtn: FC<IDropdownBtnProps> = ( props ) => {

	const { id, hint, menuItems = [], align = "end", direction = "bottom", 
		menuButton,
		className, children, ...otherProps 
	} = props;

	const itemClick = ( e: any, item: DropDownItemType ) => {
		setTimeout( () => {
			if ( item.action ) {
				item.action( e );
			}
		}, 10 );
	};

	const renderMenuItem = ( item: DropDownItemType ) => (
		<>
			{ !!item.icon && item.icon }
			{ item.title }
			{ (!!item.caret || !!item.subItem?.length) && <Caret className={st.caret} /> }
		</>
	);

	const renderItem = ( item: DropDownItemType, index: number, parentId: string = "mnu" ) => {
		if ( !!item.subItem?.length ) {
			const curId = parentId + "_subMenu_" + index;
			return (
				<SubMenu key={ "subMenu_" + index } id={curId} label={ renderMenuItem(item) }>
					{ item.subItem?.map( ( mnu, i ) => renderItem( mnu, i, curId ) ) }
				</SubMenu>
			);
		}
		else {
			return (
				<MenuItem key={ "menuItem_" + index } id={ parentId + "-" + index } className={ !!item.isActive ? st.active : "" } onClick={ e => itemClick(e, item )}>
					{ renderMenuItem(item) }
				</MenuItem>
			);
		}
	};

	const renderMenuButton: RenderProp<MenuButtonModifiers, React.ReactElement> = ( modifiers ) => (
		<MenuButton 
			className={classNames(st.ddBtn, { [st.opened]: modifiers.open } )} 
			data-tooltip-id="menu-tooltip" 
			data-tooltip-content={hint}
		>
			{children}
		</MenuButton>
	);

	return (
		<Menu 
			id={id}
			className={className}
			menuClassName={st.contextMnu}
			menuButton={ !!menuButton ? menuButton : renderMenuButton} 
			transition 
			gap={8}
			portal
			direction={direction}
			viewScroll="auto"
			position="auto"
			align={align}
			{...otherProps}
		>
			{
				menuItems.map( ( mnu, i ) => renderItem( mnu, i, id ) )
			}
		</Menu>
	);
};

export default DropdownBtn;
